import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, FormGroup, Label, Row } from 'reactstrap';
import ButtonSection from '../ProjectCreate/ButtonSection';
import api from '../../../../utils/api';
import Swal from 'sweetalert2';
import { TicketOfferListTableDataColumnType } from '../../../../Types/Application/Ecommerce/Ecommerce';
import Select, { components } from 'react-select';

const BookingTicketForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    ticketBookingId: 0,
    customerName: '',
    customerMobileNumber: '',
    bookingDate: new Date().toISOString().split('T')[0],
    address: '',
    numberOdAdults: '0',
    numberOfChildren: '0',
    ApplicationUserId: '',
    payAmount: 0,
    onlineAmount: 0,
    cashAmount: 0,
    discount_per: 0,
    discount_amt: 0,
    ticketOfferId: '',
    portal: 1,
  });

  const [offerListTableDatas, setOfferListTableDatas] = useState<TicketOfferListTableDataColumnType[]>([]);
  const [adultPlanPrice, setAdultPlanPrice] = useState(0);
  const [childPlanPrice, setChildPlanPrice] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalFree, setTotalFree] = useState(0);
  const [ticketOfferId, setTicketOfferId] = useState("");

  const getAdultPlanPrice = async () => {
    try {
      const response = await api.get('api/AdultPlan/GetAdultPlanById?adultPlanId=2');
      if (response.status === 200) {
        const adultPlan = response.data;
        if (adultPlan && adultPlan.adultPlanId) {
          setAdultPlanPrice(adultPlan.seasonalStatus === true ? adultPlan.seasonalPrice : adultPlan.planPrice);
        }
      }
    } catch (error) {
      console.error("Failed to fetch adult plan:", error);
    }
  };

  const getChildPlanPrice = async () => {
    try {
      const response = await api.get('api/AdultPlan/GetAdultPlanById?adultPlanId=1');
      if (response.status === 200) {
        const adultPlan = response.data;
        if (adultPlan && adultPlan.adultPlanId) {
          setChildPlanPrice(adultPlan.seasonalStatus === true ? adultPlan.seasonalPrice : adultPlan.planPrice);
        }
      }
    } catch (error) {
      console.error("Failed to fetch child plan:", error);
    }
  };

  const boxStyle = {
    boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
    border: "1px solid #ddd",
    background: "#fff",
    borderRadius: "10px",
  };

  const fetchOffers = async () => {
    try {
      const response = await api.get(`/api/OfferPlan/GetAllTicketOfferPlan`);
      if (response.status === 200) {
        setOfferListTableDatas(response.data.map((item: {
          ticketOfferId: any; offerName: any; freeType: any;
          adultBuy: any; childBuy: any; totalFree: any; totalCount: any; isOfferActive: any;
        }) => ({
          ticketOfferId: item.ticketOfferId,
          offerName: item.offerName,
          freeType: item.freeType,
          adultBuy: item.adultBuy,
          isOfferActive: item.isOfferActive,
          childBuy: item.childBuy,
          totalFree: item.totalFree,
          totalCount: item.totalCount,
        })));
      }
    } catch (error) {
      console.error("Failed to fetch offers list:", error);
    }
  };

  useEffect(() => {
    getAdultPlanPrice();
    getChildPlanPrice();
    fetchOffers();
  }, []);

  const options = offerListTableDatas
    .filter((item: any) => item.isOfferActive === true)
    .map((item: any) => ({
      value: item.ticketOfferId,
      label: `${item.offerName}`,
    }));

  const finalOptions = [
    { label: "No Offer", value: 0 },
    ...options
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'onlineAmount') {
      const onlineAmount = Number(value) || 0;
      const finalPayAmount = Math.max(totalAmount - formData.discount_amt, 0);
      if(onlineAmount > finalPayAmount && finalPayAmount >= 0){
        Swal.fire({ icon: 'warning', title: 'Invalid Input', text: 'Online amount cannot exceed total amount.' });
        return;
      }
      if(onlineAmount < 0){
        Swal.fire({ icon: 'warning', title: 'Invalid Input', text: 'Online amount cannot be negative.' });
        return;
      }
      setFormData({
        ...formData,
        onlineAmount,
        cashAmount: Math.max(finalPayAmount - onlineAmount, 0),
      });
      return;
    }

    if (name === 'cashAmount') {
      const cashAmount = Number(value) || 0;
      const finalPayAmount = Math.max(totalAmount - formData.discount_amt, 0);
      if(cashAmount > finalPayAmount && finalPayAmount >= 0){
        Swal.fire({ icon: 'warning', title: 'Invalid Input', text: 'Cash amount cannot exceed total amount.' });
        return;
      }
      if(cashAmount < 0){
        Swal.fire({ icon: 'warning', title: 'Invalid Input', text: 'Cash amount cannot be negative.' });
        return;
      }
      setFormData({
        ...formData,
        cashAmount,
        onlineAmount: Math.max(finalPayAmount - cashAmount, 0),
      });
      return;
    }

    if (name === 'discount_per') {
      const discountPercentage = Number(value) || 0;
      const discountAmount = (totalAmount * discountPercentage) / 100;
      const finalPayAmount = Math.max(totalAmount - discountAmount, 0);
      setFormData({
        ...formData,
        discount_per: discountPercentage,
        discount_amt: Math.round(discountAmount * 100) / 100,
        onlineAmount: 0,
        cashAmount: Math.round(finalPayAmount * 100) / 100,
      });
      return;
    }

    if (name === 'numberOdAdults' || name === 'numberOfChildren') {
      const totalAdults = name === 'numberOdAdults'
        ? parseInt(value) || 0
        : parseInt(formData.numberOdAdults) || 0;
      const totalChildren = name === 'numberOfChildren'
        ? parseInt(value) || 0
        : parseInt(formData.numberOfChildren) || 0;
      const calculated = (totalAdults * adultPlanPrice) + (totalChildren * childPlanPrice);
      const newTotalAmount = Math.max(calculated - totalFree, 0);
      setTotalAmount(newTotalAmount);
      
      // Recalculate discount amount based on the new total amount and existing discount percentage
      const discountAmount = (newTotalAmount * formData.discount_per) / 100;
      
      // If total amount is zero, reset cash and online amounts
      const updatedFormData = {
        ...formData,
        [name]: value,
        discount_amt: Math.round(discountAmount * 100) / 100,
      };
      
      if (newTotalAmount === 0) {
        updatedFormData.cashAmount = 0;
        updatedFormData.onlineAmount = 0;
      }
      
      setFormData(updatedFormData);
      return;
    }

    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getOfferDetails = async (value: any) => {
    if (!value || value === 0) {
      setTotalFree(0);
      setTicketOfferId("");
      const recalculated =
        (Number(formData?.numberOdAdults ?? 0) * adultPlanPrice) +
        (Number(formData?.numberOfChildren ?? 0) * childPlanPrice);
      const newTotal = Math.max(recalculated, 0);
      setTotalAmount(newTotal);
      
      // Reset cash and online amounts if total becomes zero
      if (newTotal === 0) {
        setFormData(prev => ({
          ...prev,
          cashAmount: 0,
          onlineAmount: 0,
        }));
      }
      return;
    }

    try {
      const response = await api.get(`/api/AdultPlan/GetTicketOfferPlans?OfferId=${value}`);
      if (response.status === 200) {
        const totalFreeAmount = response.data?.totalFreePrice ?? 0;
        const base =
          (Number(formData?.numberOdAdults ?? 0) * adultPlanPrice) +
          (Number(formData?.numberOfChildren ?? 0) * childPlanPrice);
        const newTotal = Math.max(base - totalFreeAmount, 0);
        setTotalAmount(newTotal);
        
        // Reset cash and online amounts if total becomes zero
        if (newTotal === 0) {
          setFormData(prev => ({
            ...prev,
            cashAmount: 0,
            onlineAmount: 0,
          }));
        }
        
        // ✅ FIX 1: Use the selected `value` (the offerId we queried), not response.data.offerId
        // which may be undefined or a different field name in the API response.
        setTicketOfferId(String(value));
        setTotalFree(totalFreeAmount);
      }
    } catch (error) {
      console.error("Failed to fetch offer details:", error);
    }
  };

 const refs = useRef<(HTMLElement | null)[]>([]);
 const selectRef = useRef<any>(null);

  const focusNext = (index: number) => {
    if (index === 6) {
    // Discount Amount → Offer
    selectRef.current?.focus();
    return;
  }

    refs.current[index + 1]?.focus();
  };

  const focusPrev = (index: number) => {
    if (index === 8) {
    // Address → Offer
    selectRef.current?.focus();
    return;
  }
    refs.current[index - 1]?.focus();
  };

  const CustomInput = (props: any) => (
    <components.Input
      {...props}
      onKeyDown={(e: any) => {
        if (e.key === "ArrowRight" || e.key === "Enter") {
          e.preventDefault();
          refs.current[8]?.focus(); // Address
        }

        if (e.key === "ArrowLeft") {
          e.preventDefault();
          refs.current[6]?.focus(); // Discount Amount
        }

        props.innerProps?.onKeyDown?.(e);
      }}
    />
  );

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLElement>,
    index: number
  ) => {
    if (e.key === "ArrowRight" || e.key === "Enter") {
      e.preventDefault();
      focusNext(index);
    }

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusPrev(index);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // ✅ FIX 2: Safe localStorage parse — avoids crash if 'user' key is missing
    const userData = (() => {
      try {
        return JSON.parse(localStorage.getItem('user') || '{}');
      } catch {
        return {};
      }
    })();

    const adults = parseInt(formData.numberOdAdults) || 0;
    const children = parseInt(formData.numberOfChildren) || 0;

    // ✅ FIX 3: Validation — use || not && so either-zero is caught correctly
    if (adults <= 0 && children <= 0) {
      Swal.fire({ icon: 'warning', title: 'Invalid Input', text: 'Please enter at least one adult or child.' });
      return;
    }
    if (totalAmount <= 0 || isNaN(totalAmount)) {
      Swal.fire({ icon: 'warning', title: 'Invalid Input', text: 'Total amount must be a positive number.' });
      return;
    }
    const finalPayAmount = Math.max(totalAmount - formData.discount_amt, 0);
    if(finalPayAmount <= 0){
      Swal.fire({ icon: 'warning', title: 'Invalid Input', text: 'Final pay amount must be greater than zero after discount.' });
      return;
    }
    if(formData.onlineAmount < 0 || formData.cashAmount < 0){
      Swal.fire({ icon: 'warning', title: 'Invalid Input', text: 'Online and cash amounts cannot be negative.' });
      return;
    }
    if ((formData.onlineAmount + formData.cashAmount) > finalPayAmount) {
      Swal.fire({ icon: 'warning', title: 'Invalid Input', text: 'The sum of online and cash amounts cannot exceed the total amount.' });
      return;
    }
    if(formData.onlineAmount === 0 && formData.cashAmount === 0){
      Swal.fire({ icon: 'warning', title: 'Invalid Input', text: 'Please enter an online or cash amount.' });
      return;
    }

    if (!formData.customerName || !formData.customerMobileNumber) {
      Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Please fill in all required fields.' });
      return;
    }
    if (!/^\d{10}$/.test(formData.customerMobileNumber)) {
      Swal.fire({ icon: 'warning', title: 'Invalid Mobile Number', text: 'Please enter a valid 10-digit mobile number.' });
      return;
    }

    // ✅ FIX 4: Send flat payload directly — NOT wrapped in { booking: payload }
    // Laravel's bookTicket() reads directly from $request->validate([...]) at the top level.
    const payload = {
      ticketBookingId: 0,
      customerName: formData.customerName,
      customerMobileNumber: formData.customerMobileNumber,
      address: formData.address,
      numberOdAdults: String(adults),
      numberOfChildren: String(children),
      ApplicationUserId: "f41bc1ea-f637-4672-b0e1-748e3411309d",
      staffId: userData.staffId ?? null,
      payAmount: finalPayAmount,
      bookingDate: formData.bookingDate,
      // Send numeric 0 when no offer is selected (matches Laravel: $offerId > 0 check)
      ticketOfferId: ticketOfferId !== "" ? Number(ticketOfferId) : 0,
      portal: 1,
      onlineAmount: formData.onlineAmount,
      cashAmount: formData.cashAmount,
      discountPer: String(formData.discount_per),
      discountAmt: String(formData.discount_amt),
    };

    try {
      const response = await api.post('/api/TicketBooking/AddTicketBooking', payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.status === 200 || response.status === 201) {
        Swal.fire({ icon: 'success', title: 'Success!', text: 'Ticket booked successfully.' }).then(() => {
          // navigate(`${process.env.PUBLIC_URL}/ticket/booking_history`);
          navigate(`/ticket-booking-invoice/${response.data.ticketBookingId}`);
        });
      } else {
        Swal.fire({ icon: 'warning', title: 'Unexpected Response', text: 'Please try again later.' });
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Submission Failed', text: 'Something went wrong while booking ticket.' });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="theme-form">
      <Row>
        <Col sm="4">
          <FormGroup>
            <Label>Customer Name</Label>
            <input
              name="customerName"
              type="text"
              className="form-control"
              placeholder="Enter customer name..."
              value={formData.customerName}
              required={true}
              onChange={handleChange}
              ref={(el) => void (refs.current[0] = el)}
              onKeyDown={(e) => handleKeyDown(e, 0)}
            />
          </FormGroup>
        </Col>
        <Col sm="4">
          <FormGroup>
            <Label>Customer Mobile No.</Label>
            <input
              name="customerMobileNumber"
              type="text"
              className="form-control"
              placeholder="Enter customer mobile number..."
              value={formData.customerMobileNumber}
              required={true}
              onChange={handleChange}
              ref={(el) => void (refs.current[1] = el)}
              onKeyDown={(e) => handleKeyDown(e, 1)}
            />
          </FormGroup>
        </Col>
        <Col sm="4">
          <FormGroup>
            <Label>Booking Date</Label>
            <input
              name="bookingDate"
              type="date"
              className="form-control"
              value={formData.bookingDate}
              required={true}
              min={new Date().toISOString().split("T")[0]}
              onChange={handleChange}
              ref={(el) => void (refs.current[2] = el)}
              onKeyDown={(e) => handleKeyDown(e, 2)}
            />
          </FormGroup>
        </Col>
        <Col sm="4">
          <FormGroup>
            <Label>No. Of Adults</Label>
            <input
              name="numberOdAdults"
              type="number"
              className="form-control"
              placeholder="Enter number of adults..."
              value={formData.numberOdAdults}
              onChange={handleChange}
              ref={(el) => void (refs.current[3] = el)}
              onKeyDown={(e) => handleKeyDown(e, 3)}
            />
          </FormGroup>
        </Col>
        <Col sm="4">
          <FormGroup>
            <Label>No. Of Child</Label>
            <input
              name="numberOfChildren"
              type="number"
              className="form-control"
              placeholder="Enter number of children..."
              value={formData.numberOfChildren}
              onChange={handleChange}
              ref={(el) => void (refs.current[4] = el)}
              onKeyDown={(e) => handleKeyDown(e, 4)}
            />
          </FormGroup>
        </Col>
        <Col sm="4">
          <FormGroup>
            <Label>Discount(%)</Label>
            <input
              name="discount_per"
              type="number"
              className="form-control"
              placeholder="Enter discount percentage here...(Optional)"
              value={formData.discount_per}
              ref={(el) => void (refs.current[5] = el)}
              onChange={handleChange}
              onKeyDown={(e) => handleKeyDown(e, 5)}
            />
          </FormGroup>
        </Col>
        <Col sm="4">
          <FormGroup>
            <Label>Discount Amount</Label>
            <input
              name="discount_amt"
              type="number"
              className="form-control"
              value={formData.discount_amt}
              onChange={handleChange}
              ref={(el) => void (refs.current[6] = el)}
              onKeyDown={(e) => handleKeyDown(e, 6)}
            />
          </FormGroup>
        </Col>
        <Col sm="4">
          <FormGroup>
            <Label>Offer</Label>
            <div className="p-2" style={boxStyle}>
              <Select
                ref={selectRef}
                options={finalOptions}
                value={
                  ticketOfferId === ""
                    ? finalOptions[0]
                    : finalOptions.find((opt) => opt.value === Number(ticketOfferId)) || null
                }
                onChange={(selectedOption) => {
                  const value = selectedOption?.value ?? 0;
                  setTicketOfferId(String(value === 0 ? "" : value));
                  getOfferDetails(value);
                }}
                className="fw-bold"
                placeholder="Select Offer"
                name="ticketOfferId"
                components={{
                  Input: CustomInput,
                }}
                isSearchable
                isClearable
              />
            </div>
          </FormGroup>
        </Col>
        <Col sm="4">
          <FormGroup>
            <Label>Address</Label>
            <textarea
              name="address"
              className="form-control"
              placeholder="Enter customer address..."
              value={formData.address}
              onChange={handleChange}
              ref={(el) => void (refs.current[8] = el)}
              onKeyDown={(e) => handleKeyDown(e, 8)}
            ></textarea>
          </FormGroup>
        </Col>
        <Col sm="4">
          <FormGroup>
            <Label>Pay Amount</Label>
            <input
              type="text"
              className="form-control"
              value={totalAmount > 0 ? `₹ ${Math.max(totalAmount - formData.discount_amt, 0)}` : "₹ 0"}
              readOnly={true}
              ref={(el) => void (refs.current[9] = el)}
              onKeyDown={(e) => handleKeyDown(e, 9)}
            />
          </FormGroup>
        </Col>
        <Col sm="4">
          <FormGroup>
            <Label>Online Amount</Label>
            <input
              name="onlineAmount"
              type="number"
              className="form-control"
              placeholder="Enter online amount..."
              value={formData.onlineAmount}
              onChange={handleChange}
              ref={(el) => void (refs.current[10] = el)}
              onKeyDown={(e) => handleKeyDown(e, 10)}
            />
          </FormGroup>
        </Col>
        <Col sm="4">
          <FormGroup>
            <Label>Cash Amount</Label>
            <input
              name="cashAmount"
              type="number"
              className="form-control"
              placeholder="Enter cash amount..."
              value={formData.cashAmount}
              onChange={handleChange}
              ref={(el) => void (refs.current[11] = el)}
              onKeyDown={(e) => handleKeyDown(e, 11)}
            />
          </FormGroup>
        </Col>
      </Row>
      <ButtonSection cancel_url={`/ticket/booking_history`} />
    </form>
  );
};

export default BookingTicketForm;